import { Button, Select, SelectItem, Chip, useDisclosure } from "@heroui/react";
import { Trans, useLingui } from "@lingui/react/macro";
import { Languages, Plus } from "lucide-react";
import { Language } from "@/server-types";
import { UseFormReturnType } from "@mantine/form";
import { FormValues } from "../../types";
import {
  LocalizationForm,
  LocalizedArray,
} from "@/Components/NextBase/LocalizationForm";
import { getLocalizedValue } from "@/utils";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeColorPicker } from "@/Components/LimeColorPicker";
import { api } from "@/lib/api-client";
import { CategoryForm } from "./CategoriesTab";
import { TagForm } from "./TagsTab";

const GeneralTab = ({
  form,
  defaultLanguage,
}: {
  form: UseFormReturnType<FormValues>;
  defaultLanguage: Language;
}) => {
  const { t } = useLingui();

  const {
    isOpen: isNameLocalizationOpen,
    onOpen: onNameLocalizationOpen,
    onClose: onNameLocalizationClose,
  } = useDisclosure();

  const {
    isOpen: isDescriptionLocalizationOpen,
    onOpen: onDescriptionLocalizationOpen,
    onClose: onDescriptionLocalizationClose,
  } = useDisclosure();

  const {
    isOpen: isCategoryFormOpen,
    onOpen: onCategoryFormOpen,
    onClose: onCategoryFormClose,
  } = useDisclosure();

  const {
    isOpen: isTagFormOpen,
    onOpen: onTagFormOpen,
    onClose: onTagFormClose,
  } = useDisclosure();

  const { data: tags = [], isFetching: isFetchingTags } =
    api.subscription.useGetSubscriptionTags();
  const {
    data: categories = { categories: [] },
    isFetching: isFetchingCategories,
  } = api.subscription.useGetSubscriptionCategories();

  const getSelectedTags = (selectedIds: number[]) => {
    return tags.filter((tag) => selectedIds.includes(tag.id));
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <LimeInput
          type="text"
          variant="bordered"
          label={t`Ime naročnine`}
          labelPlacement="outside"
          {...form.getInputProps("nameLocalized")}
          placeholder={t`Vnesite ime naročnine`}
          value={getLocalizedValue(form.values.nameLocalized)}
          data-identifier="subscription-name-input"
          onChange={(e) => {
            const newNameLocalized = [...form.values.nameLocalized];
            const defaultLangIndex = newNameLocalized.findIndex(
              (n) => n.language === defaultLanguage,
            );
            newNameLocalized[defaultLangIndex] = {
              ...newNameLocalized[defaultLangIndex],
              value: e.target.value,
            };
            form.setFieldValue("nameLocalized", newNameLocalized);
          }}
          endContent={
            <Button
              size="sm"
              variant="light"
              isIconOnly
              onPress={onNameLocalizationOpen}
            >
              <Languages size={16} />
            </Button>
          }
        />
      </div>
      <div>
        <LimeInput
          type="textarea"
          variant="bordered"
          label={t`Opis naročine`}
          labelPlacement="outside"
          placeholder={t`Vnesite opis naročine`}
          value={getLocalizedValue(form.values.descriptionLocalized)}
          data-identifier="subscription-description-input"
          onChange={(e) => {
            const newDescriptionLocalized = [
              ...form.values.descriptionLocalized,
            ];
            const defaultLangIndex = newDescriptionLocalized.findIndex(
              (d) => d.language === defaultLanguage,
            );
            newDescriptionLocalized[defaultLangIndex] = {
              ...newDescriptionLocalized[defaultLangIndex],
              value: e.target.value,
            };
            form.setFieldValue("descriptionLocalized", newDescriptionLocalized);
          }}
          endContent={
            <Button
              size="sm"
              variant="light"
              isIconOnly
              onPress={onDescriptionLocalizationOpen}
            >
              <Languages size={16} />
            </Button>
          }
        />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-sm font-medium">{t`Oznake`}</label>
          <Button
            variant="flat"
            size="sm"
            startContent={<Plus size={16} />}
            onPress={onTagFormOpen}
            className="bg-gray-50 text-green-700"
            data-identifier="add-tag-btn"
          >
            <Trans>Dodaj novo</Trans>
          </Button>
        </div>
        <Select
          variant="bordered"
          placeholder={t`Izberite oznake`}
          selectionMode="multiple"
          isMultiline
          isLoading={isFetchingTags}
          selectedKeys={new Set(form.values.tagIds.map((id) => id.toString()))}
          onSelectionChange={(keys) => {
            form.setFieldValue("tagIds", Array.from(keys).map(Number));
          }}
          renderValue={(items) => {
            const selectedTags = getSelectedTags(form.values.tagIds);
            return (
              <div className="flex flex-wrap gap-2">
                {selectedTags.map((tag) => (
                  <Chip
                    key={tag.id}
                    onClose={() => {
                      form.setFieldValue(
                        "tagIds",
                        form.values.tagIds.filter((id) => id !== tag.id),
                      );
                    }}
                  >
                    {getLocalizedValue(tag.nameLocalized)}
                  </Chip>
                ))}
              </div>
            );
          }}
        >
          {tags.map((tag) => (
            <SelectItem
              key={tag.id.toString()}
              textValue={getLocalizedValue(tag.nameLocalized)}
            >
              <div className="flex items-center gap-2">
                <div
                  className="h-3 w-3 rounded-full"
                  style={{
                    backgroundColor: tag.color || "#000000",
                  }}
                />
                {getLocalizedValue(tag.nameLocalized)}
              </div>
            </SelectItem>
          ))}
        </Select>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-sm font-medium">{t`Kategorija`}</label>
          <Button
            variant="flat"
            size="sm"
            startContent={<Plus size={16} />}
            onPress={onCategoryFormOpen}
            className="bg-gray-50 text-green-700"
            data-identifier="add-category-btn"
          >
            <Trans>Dodaj novo</Trans>
          </Button>
        </div>
        <Select
          variant="bordered"
          placeholder={t`Izberite kategorijo`}
          isLoading={isFetchingCategories}
          selectedKeys={
            form.values.categoryId ? [form.values.categoryId.toString()] : []
          }
          onSelectionChange={(keys) => {
            const key = Array.from(keys)[0];
            form.setFieldValue("categoryId", key ? Number(key) : null);
          }}
          className="w-full"
        >
          {categories.categories.map((category) => (
            <SelectItem
              key={category.id.toString()}
              textValue={
                category.nameLocalized.find(
                  (n) => n.language === defaultLanguage,
                )?.value ||
                category.nameLocalized.find((n) => n.language === "sl")
                  ?.value ||
                category.nameLocalized[0]?.value ||
                ""
              }
            >
              <div className="flex items-center gap-2">
                {category.nameLocalized.find(
                  (n) => n.language === defaultLanguage,
                )?.value ||
                  category.nameLocalized.find((n) => n.language === "sl")
                    ?.value ||
                  category.nameLocalized[0]?.value ||
                  ""}
              </div>
            </SelectItem>
          ))}
        </Select>
      </div>

      <LimeColorPicker
        label={t`Barva`}
        value={form.values.color}
        onChange={(color) => form.setFieldValue("color", color)}
        data-identifier="subscription-color-picker"
      />

      <LocalizationForm
        opened={isNameLocalizationOpen}
        localized={form.values.nameLocalized.map(
          (n: { language: Language; value: string }) => ({
            language: n.language,
            value: n.value,
          }),
        )}
        save={(newLocalizations: LocalizedArray) => {
          form.setFieldValue("nameLocalized", newLocalizations);
          onNameLocalizationClose();
        }}
        cancel={onNameLocalizationClose}
        inputLabel={t`Ime naročnine`}
      />

      <LocalizationForm
        opened={isDescriptionLocalizationOpen}
        localized={form.values.descriptionLocalized}
        save={(newLocalizations: LocalizedArray) => {
          form.setFieldValue("descriptionLocalized", newLocalizations);
          onDescriptionLocalizationClose();
        }}
        cancel={onDescriptionLocalizationClose}
        inputLabel={t`Opis naročnine`}
      />

      <CategoryForm
        isOpen={isCategoryFormOpen}
        onClose={onCategoryFormClose}
        categoryId={undefined}
      />

      <TagForm
        isOpen={isTagFormOpen}
        onClose={onTagFormClose}
        tagId={undefined}
      />
    </div>
  );
};

export default GeneralTab;
